import React, { useState, useEffect, FormEvent } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { createEmpresa } from "../../../api/empresaApi";
import Formulario, { FormField } from "../../../components/Formulario";
import { toast } from "react-toastify";
import { getGrupos } from "../../../api/grupoApi";
import "react-toastify/dist/ReactToastify.css";
import { createGrupoFerramenta, getFerramentas } from "../../../api/ferramentasApi";
import { buscaGrupo } from "../../../types/grupo";
import { buscaFerramenta } from "../../../types/ferramenta";

const AdicionarEmpresa: React.FC = () => {
  const [cnpjEmpresa, setCnpjEmpresa] = useState<string>("");
  const [nomeEmpresa, setNomeEmpresa] = useState<string>("");
  const [idGrupo, setIdGrupo] = useState<buscaGrupo | null>(null); 
  const [grupos, setGrupos] = useState<buscaGrupo[]>([]);
  const [logoEmpresa, setLogoEmpresa] = useState<string>("");
  const [enderecoEmpresa, setEnderecoEmpresa] = useState<string>("");
  const [cepEmpresa, setCepEmpresa] = useState<string>("");
  const [bairroEmpresa, setBairroEmpresa] = useState<string>("");
  const [numeroEmpresa, setNumeroEmpresa] = useState<string>("");
  const [cidadeEmpresa, setCidadeEmpresa] = useState<string>("");
  const [ufEmpresa, setUfEmpresa] = useState<string>("");
  const [telefoneEmpresa, setTelefoneEmpresa] = useState<string>("");
  const [codigoDmsEmpresa, setCodigoDmsEmpresa] = useState<string>("");
  const [codigoDmsRevenda, setCodigoDmsRevenda] = useState<string>("");
  const [bandeiraEmpresa, setBandeiraEmpresa] = useState<string>("");
  const [ferramentas, setFerramentas] = useState<buscaFerramenta[]>([]);
  const [ferramentasSelecionadas, setFerramentasSelecionadas] = useState<buscaFerramenta[]>([]); 
  const [loading, setLoading] = useState<boolean>(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const fetchGrupos = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          throw new Error("Token não encontrado");
        }
        const gruposData = await getGrupos(token);
        setGrupos(gruposData);

        if (location.state && location.state.grupoId) {
          const grupoInicial = gruposData.find(
            (g) => g.id === location.state.grupoId
          );
          setIdGrupo(grupoInicial || null); 
        }
      } catch (error) {
        toast.error("Erro ao buscar grupos!");
        console.error("Erro ao buscar grupos:", error);
      }
    };

    const fetchFerramentas = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          throw new Error("Token não encontrado");
        }
        const ferramentasData = await getFerramentas(token);
        setFerramentas(ferramentasData);
      } catch (error) {
        toast.error("Erro ao buscar ferramentas!");
        console.error("Erro ao buscar ferramentas:", error);
      }
    };

    fetchGrupos();
    fetchFerramentas();
  }, [location.state]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("Token não encontrado");
      }

      const formData = new FormData();
      formData.append("cnpj_empresa", cnpjEmpresa);
      formData.append("nome_empresa", nomeEmpresa);
      formData.append("id_grupo", idGrupo?.id.toString() || ""); 
      formData.append("logo_empresa", logoEmpresa);
      formData.append("endereco_empresa", enderecoEmpresa);
      formData.append("cep_empresa", cepEmpresa);
      formData.append("bairro_empresa", bairroEmpresa);
      formData.append("numero_empresa", numeroEmpresa);
      formData.append("cidade_empresa", cidadeEmpresa);
      formData.append("uf_empresa", ufEmpresa);
      formData.append("telefone_empresa", telefoneEmpresa);
      formData.append("codigo_dms_empresa", codigoDmsEmpresa);
      formData.append("codigo_dms_revenda", codigoDmsRevenda);
      formData.append("bandeira_empresa", bandeiraEmpresa);

      const empresaCriada = await createEmpresa(token, formData);

      if (ferramentasSelecionadas.length > 0) {
        await Promise.all(
          ferramentasSelecionadas.map(async (ferramenta) => {
            await createGrupoFerramenta(token, empresaCriada.id, ferramenta.id);
          })
        );
      }

      toast.success("Empresa criada com sucesso!");
      navigate("/empresas");
    } catch (error) {
      toast.error("Erro ao adicionar empresa!");
      console.error("Erro ao adicionar empresa:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleGrupoChange = (event: React.SyntheticEvent, value: buscaGrupo | null) => {
    setIdGrupo(value);
  };

  const handleFerramentaChange = (event: React.SyntheticEvent, value: buscaFerramenta[]) => {
    setFerramentasSelecionadas(value);
  };

  const fields: FormField[] = [
    {
      label: "CNPJ da Empresa",
      type: "text",
      value: cnpjEmpresa,
      required: true,
      onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
        setCnpjEmpresa(e.target.value),
    },
    {
      label: "Nome da Empresa",
      type: "text",
      value: nomeEmpresa,
      required: true,
      onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
        setNomeEmpresa(e.target.value),
    },
    {
      label: "Grupo",
      type: "autocomplete",
      value: idGrupo,
      required: true,
      onChangeAutocomplete: handleGrupoChange,
      options: grupos,
      getOptionLabel: (option: buscaGrupo) => option.nome_grupo, 
    },
    {
      label: "Logo da Empresa",
      type: "text",
      value: logoEmpresa,
      required: true,
      onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
        setLogoEmpresa(e.target.value),
    },
    {
      label: "Endereço",
      type: "text",
      value: enderecoEmpresa,
      required: false,
      onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
        setEnderecoEmpresa(e.target.value),
    },
    {
      label: "CEP",
      type: "text",
      value: cepEmpresa,
      required: false,
      onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
        setCepEmpresa(e.target.value),
    },
    {
      label: "Bairro",
      type: "text",
      value: bairroEmpresa,
      required: false,
      onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
        setBairroEmpresa(e.target.value),
    },
    {
      label: "Número",
      type: "text",
      value: numeroEmpresa,
      required: false,
      onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
        setNumeroEmpresa(e.target.value),
    },
    {
      label: "Cidade",
      type: "text",
      value: cidadeEmpresa,
      required: false,
      onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
        setCidadeEmpresa(e.target.value),
    },
    {
      label: "UF",
      type: "text",
      value: ufEmpresa,
      required: false,
      onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
        setUfEmpresa(e.target.value),
    },
    {
      label: "Telefone",
      type: "text",
      value: telefoneEmpresa,
      required: false,
      onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
        setTelefoneEmpresa(e.target.value),
    },
    {
      label: "Código DMS Empresa",
      type: "text",
      value: codigoDmsEmpresa,
      required: false,
      onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
        setCodigoDmsEmpresa(e.target.value),
      subtitle:
        "Usado quando é uma empresa que usa o Integrador pra conectar via Banco de dados e é necessário o preenchimento do código da empresa cadastrado no banco do cliente.",
    },
    {
      label: "Código DMS Revenda",
      type: "text",
      value: codigoDmsRevenda,
      required: false,
      onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
        setCodigoDmsRevenda(e.target.value),
      subtitle:
        "Usado quando é uma empresa que usa o Integrador ou usa a API da LINX e é necessário o preenchimento do código da revenda cadastrado no banco do cliente.",
    },
    {
      label: "Bandeira",
      type: "text",
      value: bandeiraEmpresa,
      required: false,
      onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
        setBandeiraEmpresa(e.target.value),
      subtitle:
        "Usado quando é a API LINX para poder realizar algumas requisições via API é necessário esse preenchimento. Exemplo: \"CHER\"",
    },
    {
      label: "Ferramentas",
      type: "autocomplete",
      value: ferramentasSelecionadas,
      required: false,
      onChangeAutocomplete: handleFerramentaChange,
      options: ferramentas,
      getOptionLabel: (option: buscaFerramenta) => option.nome_ferramenta, 
      multiple: true,
    },
  ];

  return (
    <Formulario
      title="Adicionar Empresa"
      fields={fields}
      onSubmit={handleSubmit}
      loading={loading}
      onBack={handleBack}
    />
  );
};

export default AdicionarEmpresa;